<template>
  <div
    class="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100"
    :class="{ 'flex-col': screenSize == 'mobile' }"
  >
    <img
      v-if="broker.url && screenSize == 'mobile'"
      class="broker-logo"
      :src="broker.url"
      alt="broker logo"
    />
    <div class="mx-4 flex w-full max-w-6xl">
      <div
        class="hidden rounded-l-3xl bg-cover bg-center lg:flex lg:w-1/2"
        :style="bgStyle"
      >
        <div
          class="flex w-full flex-col items-center justify-center px-12 text-white"
        >
          <template v-if="isLoading">
            <div class="mb-5 h-11 w-40 animate-pulse rounded bg-gray-200"></div>
            <div class="mb-6 h-8 w-48 animate-pulse rounded bg-gray-200"></div>
          </template>
          <template v-else>
            <img
              v-if="broker.url"
              class="broker-logo"
              :src="broker.url"
              alt="broker logo"
            />
            <h1 class="mb-6 text-4xl font-bold text-black">
              {{ broker.legal_name }}
            </h1>
          </template>
          <div
            v-if="isLoading"
            class="mb-5 h-11 w-full animate-pulse rounded bg-gray-200"
          ></div>
          <p
            v-else
            class="text-center text-lg text-gray-700"
          >
            Your all-in-one solution for efficient freight management and load
            booking
          </p>
        </div>
      </div>
      <section
        class="w-full rounded-br-3xl rounded-tr-3xl lg:w-1/2"
        style="background-color: #ebf4f5"
      >
        <div class="rounded-3xl bg-white p-12 shadow-xl backdrop-blur-sm">
          <div class="mx-auto max-w-md space-y-8">
            <div class="text-center">
              <h2 class="mb-2 text-3xl font-bold text-gray-900">Welcome</h2>
              <p class="-mb-3 text-gray-600">Log in to your carrier portal</p>
            </div>

            <form
              class="space-y-6"
              @submit.prevent="handleLogin"
            >
              <div>
                <label
                  for="email"
                  class="mb-1 block text-left text-sm font-medium text-gray-700"
                >
                  Email
                </label>
                <input
                  id="email"
                  v-model="email"
                  type="email"
                  required
                  class="w-full rounded border px-3 py-3 text-lg"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label
                  for="password"
                  class="mb-1 block text-left text-sm font-medium text-gray-700"
                >
                  Password
                </label>
                <input
                  id="password"
                  v-model="password"
                  type="password"
                  required
                  class="w-full rounded border px-3 py-3 text-lg"
                  placeholder="••••••••"
                />
              </div>

              <button
                type="submit"
                class="w-full rounded-md bg-gray-700 py-3 text-lg text-white transition-all duration-200 hover:bg-gray-600"
              >
                Sign in with Email
              </button>
            </form>
            <div class="relative">
              <div class="absolute inset-0 flex items-center">
                <div class="w-full border-t border-gray-300"></div>
              </div>
              <div class="relative flex justify-center text-sm">
                <span class="bg-white px-2 text-gray-500">
                  Or continue with
                </span>
              </div>
            </div>

            <button
              class="hover:yellow-hovered flex w-full items-center justify-center gap-2 rounded-md bg-neutral-subtle-yellow py-3 text-lg shadow-md transition-all duration-200 hover:shadow-lg"
              @click="handleSSO"
            >
              <svg
                style="margin-left: 6px"
                width="22"
                height="22"
                viewBox="0 0 35 22"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M22.7139 21.802H34.4453L25.7554 0.199219H19.8897L20.8673 7.90674H14.024L15.1103 0.199219H9.24458L0.554688 21.802H12.286L13.2636 14.0945H21.7363L22.7139 21.802Z"
                  fill="black"
                ></path>
              </svg>
              Sign In with Highway
            </button>

            <div class="text-center text-sm text-gray-600">
              <span>Demo Version 1.0</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { useRouter } from 'vue-router';
import { ref, onMounted, computed } from 'vue';
import { useScreenSize } from '@/composables/useScreenSize';

import { useStore } from 'vuex';
const store = useStore();
const router = useRouter();
const email = ref('');
const password = ref('');
const isLoading = ref(true);
const { screenSize } = useScreenSize();

const broker = computed(() => {
  return store.state.brokers.logo;
});

onMounted(async () => {
  const clientId = localStorage.getItem('client_id');

  if (!clientId) {
    window.location.href = `${window.location.origin}/onboarding/`;
    return;
  }

  await store.dispatch('brokers/getLogoByClientId', clientId);

  const url = new URL(window.location.href);
  if (url.search || url.hash) {
    window.history.replaceState({}, '', url.origin + url.pathname);
  }

  isLoading.value = false;
});

function handleLogin() {}

function handleSSO() {
  const redirectSSODemo = localStorage.getItem('redirect_sso_demo');

  if (redirectSSODemo) {
    localStorage.removeItem('redirect_sso_demo');
    return router.push(redirectSSODemo);
  }
}

const bgStyle = `background-image: linear-gradient(to left, #EBF4F5, #B5C6E0)`;
</script>

<style scoped>
.yellow-hovered {
  background-color: #dfb007;
}
.animate-fade-in {
  animation: fadeIn 1s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(5%);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.broker-logo {
  margin: 0 auto;
  margin-bottom: 1.25rem;
  max-width: 60%;
  max-height: 60px;
}
</style>
